import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, AlertOctagon, Download } from "lucide-react";
import { BarChart, Bar as RBar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api, apiError, downloadFile } from "@/lib/api";
import { fmtNum, today } from "@/lib/format";
import { Kpi, Panel, Badge2, Bar, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BAND_TONE = { Exceeded: "good", Met: "good", "On Track": "primary", Underperformed: "bad", "No Targets": "muted" };

function heatColor(score) {
  if (score === null || score === undefined) return "bg-muted";
  if (score >= 110) return "bg-emerald-600";
  if (score >= 100) return "bg-emerald-500";
  if (score >= 80) return "bg-amber-500";
  if (score >= 60) return "bg-orange-500";
  return "bg-red-500";
}

export default function KPIPage() {
  const { pid } = useParams();
  const [dash, setDash] = useState(null);
  const [targets, setTargets] = useState([]);
  const [period, setPeriod] = useState("daily");
  const [dialog, setDialog] = useState(null);
  const [meta, setMeta] = useState({ trades: {} });
  const [empForm, setEmpForm] = useState({ name: "", emp_code: "", trade: "Fitter", skill_level: "skilled", team: "Team A", designation: "Workman", contact: "" });
  const [tForm, setTForm] = useState({ subject_id: "", metric: "Output Qty", unit: "Nos", period: "daily", period_start: today(), period_end: today(), target_value: "", actual_value: 0 });

  const load = () => {
    api.get("/kpi/dashboard", { params: { project_id: pid } }).then(({ data }) => setDash(data));
    api.get("/kpi/targets", { params: { project_id: pid, period } }).then(({ data }) => setTargets(data));
    api.get("/meta").then(({ data }) => setMeta(data));
  };
  useEffect(() => { load(); }, [pid, period]);

  const saveEmp = async () => {
    try { await api.post("/kpi/employees", { project_id: pid, ...empForm }); toast.success("Employee added"); setDialog(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveTarget = async () => {
    try {
      await api.post("/kpi/targets", { project_id: pid, subject_type: "employee", ...tForm, target_value: Number(tForm.target_value), actual_value: Number(tForm.actual_value) });
      toast.success("KPI target set");
      setDialog(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const updateActual = async (id, value) => {
    try { await api.put(`/kpi/targets/${id}`, { actual_value: Number(value) }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!dash) return <div className="text-sm text-muted-foreground">Loading performance analytics…</div>;

  return (
    <div data-testid="kpi-page">
      <PageHead title="Employee & Team KPI Performance" subtitle="Daily, weekly and monthly targets with auto-scoring, heatmaps and underperformance flags.">
        <Button variant="outline" onClick={() => setDialog("employee")} data-testid="kpi-add-employee-btn"><Plus className="w-4 h-4 mr-1.5" /> Add employee</Button>
        <Button onClick={() => setDialog("target")} data-testid="kpi-add-target-btn"><Plus className="w-4 h-4 mr-1.5" /> Set KPI target</Button>
        <Button variant="outline" onClick={() => downloadFile(`/reports/excel/${pid}`, "performance_review.xlsx")} data-testid="kpi-export-btn"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
      </PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-3">
        <Kpi label="Headcount tracked" value={dash.summary.headcount} testId="kpi-headcount" />
        <Kpi label="Avg attainment" value={`${dash.summary.avg_attainment}%`} tone={dash.summary.avg_attainment >= 95 ? "good" : "warn"} testId="kpi-avg-attainment" />
        <Kpi label="Targets set" value={dash.summary.targets_set} />
        <Kpi label="Exceeding target" value={dash.summary.exceeded} tone="good" />
        <Kpi label="Flagged" value={dash.summary.flagged} tone="bad" testId="kpi-flagged" />
        <Kpi label="Missing logs" value={dash.summary.missing_logs} tone="warn" />
      </div>

      <div className="grid xl:grid-cols-3 gap-3">
        <Panel title="Team comparison" testId="kpi-team-panel">
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dash.teams} margin={{ top: 6, left: -18 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="team" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} unit="%" stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <RBar dataKey="avg_score" name="Attainment %" radius={[2, 2, 0, 0]}>
                  {dash.teams.map((t, i) => <Cell key={i} fill={t.avg_score >= 100 ? "#10B981" : t.avg_score >= 80 ? "hsl(var(--primary))" : "#EF4444"} />)}
                </RBar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="14-day performance heatmap" className="xl:col-span-2" testId="kpi-heatmap-panel">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase text-muted-foreground pb-1">Employee</th>
                  {dash.days.map((d) => <th key={d} className="text-[9px] text-muted-foreground font-normal pb-1">{d.slice(8)}</th>)}
                </tr>
              </thead>
              <tbody>
                {dash.heatmap.map((row) => (
                  <tr key={row.id}>
                    <td className="text-[12px] pr-2 whitespace-nowrap max-w-[130px] truncate">{row.name}</td>
                    {row.cells.map((c, i) => (
                      <td key={i} className="p-0.5">
                        <div className={`w-full h-4 rounded-sm ${heatColor(c.score)}`} title={`${c.date}: ${c.score ?? "no log"}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded-sm" /> &lt;60%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-500 rounded-sm" /> 60-80%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded-sm" /> 80-100%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded-sm" /> 100%+</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-muted rounded-sm" /> no log</span>
          </div>
        </Panel>
      </div>

      <div className="grid xl:grid-cols-3 gap-3 mt-3">
        <Panel title="Individual profile cards" testId="kpi-profiles-panel">
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {dash.employees.map((e) => (
              <div key={e.id} className="border border-border p-2.5 flex items-center gap-3" data-testid={`employee-card-${e.id}`}>
                <div className="w-8 h-8 bg-primary/15 text-primary flex items-center justify-center text-[12px] font-semibold rounded-sm">
                  {e.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{e.name}</div>
                  <div className="text-[11px] text-muted-foreground">{e.trade} · {e.team}</div>
                </div>
                <div className="w-24"><Bar pct={e.avg_score} /></div>
                <Badge2 tone={BAND_TONE[e.band]}>{e.band}</Badge2>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Automated alerts & flags" testId="kpi-alerts-panel">
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {dash.alerts.map((a, i) => (
              <div key={i} className="border border-border p-2.5 flex items-start gap-2" data-testid={`kpi-alert-${i}`}>
                <AlertOctagon className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[13px] font-medium">{a.name} <span className="text-muted-foreground font-normal">· {a.trade}</span></div>
                  <div className="text-[11px] text-muted-foreground">{a.reason} {a.score ? `(${a.score}%)` : ""}</div>
                </div>
              </div>
            ))}
            {dash.alerts.length === 0 ? <p className="text-sm text-muted-foreground">No performance flags.</p> : null}
          </div>
        </Panel>

        <Panel title="KPI target register" testId="kpi-targets-panel"
          action={
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-7 w-28 text-[12px]" data-testid="kpi-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["daily", "weekly", "monthly"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          }>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full dense-table">
              <thead className="sticky-head"><tr><th>Employee</th><th>Metric</th><th className="text-right">Target</th><th className="text-right">Actual</th><th className="text-right">Var</th><th>Score</th></tr></thead>
              <tbody>
                {targets.slice(0, 120).map((t) => (
                  <tr key={t.id} data-testid={`target-row-${t.id}`}>
                    <td className="max-w-[120px] truncate">{t.subject_name}</td>
                    <td className="text-muted-foreground">{t.metric}</td>
                    <td className="num text-right">{fmtNum(t.target_value, 1)}</td>
                    <td className="text-right">
                      <Input
                        className="h-6 w-16 text-right num text-[12px] px-1"
                        defaultValue={t.actual_value}
                        onBlur={(e) => updateActual(t.id, e.target.value)}
                        data-testid={`target-actual-input-${t.id}`}
                      />
                    </td>
                    <td className={`num text-right ${t.variance >= 0 ? "text-emerald-500" : "text-red-500"}`}>{fmtNum(t.variance, 1)}</td>
                    <td><Badge2 tone={BAND_TONE[t.band]}>{t.score_pct}%</Badge2></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog === "employee" ? "Add employee" : "Set KPI target"}</DialogTitle></DialogHeader>
          {dialog === "employee" ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {[["name", "Full name"], ["emp_code", "Employee code"], ["team", "Team"], ["contact", "Contact"]].map(([k, l]) => (
                <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                  <Input value={empForm[k]} onChange={(e) => setEmpForm({ ...empForm, [k]: e.target.value })} data-testid={`employee-${k}-input`} /></div>
              ))}
              <div className="space-y-1.5"><Label className="text-xs uppercase">Skill level</Label>
                <Select value={empForm.skill_level} onValueChange={(v) => setEmpForm({ ...empForm, skill_level: v, trade: (meta.trades[v] || ["Helper"])[0] })}>
                  <SelectTrigger data-testid="employee-skill-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(meta.trades || {}).map((k) => <SelectItem key={k} value={k}>{k.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase">Trade</Label>
                <Select value={empForm.trade} onValueChange={(v) => setEmpForm({ ...empForm, trade: v })}>
                  <SelectTrigger data-testid="employee-trade-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta.trades[empForm.skill_level] || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Employee</Label>
                <Select value={tForm.subject_id} onValueChange={(v) => setTForm({ ...tForm, subject_id: v })}>
                  <SelectTrigger data-testid="target-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{dash.employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.trade}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase">Period</Label>
                <Select value={tForm.period} onValueChange={(v) => setTForm({ ...tForm, period: v })}>
                  <SelectTrigger data-testid="target-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["daily", "weekly", "monthly"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
              {[["metric", "Metric"], ["unit", "Unit"], ["target_value", "Target value"], ["actual_value", "Actual value"]].map(([k, l]) => (
                <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                  <Input value={tForm[k]} onChange={(e) => setTForm({ ...tForm, [k]: e.target.value })} data-testid={`target-${k}-input`} /></div>
              ))}
              <div className="space-y-1.5"><Label className="text-xs uppercase">Period start</Label>
                <Input type="date" value={tForm.period_start} onChange={(e) => setTForm({ ...tForm, period_start: e.target.value })} data-testid="target-start-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase">Period end</Label>
                <Input type="date" value={tForm.period_end} onChange={(e) => setTForm({ ...tForm, period_end: e.target.value })} data-testid="target-end-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={dialog === "employee" ? saveEmp : saveTarget} data-testid="kpi-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
